import React from 'react';
import { Button } from 'semantic-ui-react';
import styled from 'styled-components';
import CopilotDisclaimerLink from './CopilotDisclaimerLink';
import CopilotMessage, { Message } from './CopilotMessage';
import AwaitingQuery from './AwaitingQuery';

export interface SuggestedPrompt {
  id?: string;
  title?: string;
  prompt: string;
  displayText?: string;
  description?: string;
}

interface MessagesAreaProps {
  threadKey?: number;
  connectionError: string | null;
  isConnected: boolean;
  messages: Message[];
  isProcessingPrompt: boolean;
  aiConnectionStatus: 'connected' | 'disconnected' | 'processing' | 'idle';
  messagesEndRef: React.RefObject<HTMLDivElement>;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  onRetry: () => void;
  suggestedPrompts?: SuggestedPrompt[];
  onPromptSelect?: (prompt: string) => void;
  onFileLinkClick?: (payload: { href: string; fileId?: string; fileUrl?: string; label: string }) => void;
}

const MessagesArea: React.FC<MessagesAreaProps> = ({
  threadKey,
  connectionError,
  isConnected,
  messages,
  isProcessingPrompt,
  messagesEndRef,
  scrollContainerRef,
  onRetry,
  suggestedPrompts,
  onPromptSelect,
  onFileLinkClick,
}) => (
  <MessagesContainer ref={scrollContainerRef}>
    {!threadKey ? (
      <>
        <WelcomeMessage>
          <WelcomeTitle>Start a conversation!</WelcomeTitle>
          <WelcomeText>
            Ask me anything about your culture data
          </WelcomeText>
          {suggestedPrompts && suggestedPrompts.length > 0 && (
            <PromptSection>
              <PromptSectionTitle>Suggested Prompts</PromptSectionTitle>
              <PromptList>
                {suggestedPrompts.map((suggestion, index) => (
                  <PromptCard
                    key={suggestion.id ?? `${index}-${suggestion.prompt.slice(0, 20)}`}
                    onClick={() => onPromptSelect?.(suggestion.prompt)}
                  >
                    {suggestion.title && <PromptCardTitle>{suggestion.title}</PromptCardTitle>}
                    <PromptCardText>{suggestion.displayText ?? suggestion.prompt}</PromptCardText>
                  </PromptCard>
                ))}
              </PromptList>
            </PromptSection>
          )}
          <WelcomeFooter>
            <Disclaimer>
              <CopilotDisclaimerLink>
                AI responses can be inaccurate, click here to learn more...
              </CopilotDisclaimerLink>
            </Disclaimer>
          </WelcomeFooter>
        </WelcomeMessage>
      </>
    ) : connectionError ? (
      <ErrorMessage>
        <ErrorText>{connectionError}</ErrorText>
        <Button size="small" onClick={onRetry}>
          Retry
        </Button>
      </ErrorMessage>
    ) : !isConnected ? (
      <ConnectingMessage>Connecting...</ConnectingMessage>
    ) : (
      <>
        {messages?.map((message: Message, index: React.Key | null | undefined) => (
          <CopilotMessage key={index} message={message} onFileLinkClick={onFileLinkClick} />
        ))}
        {isProcessingPrompt && <AwaitingQuery context_length={100} maxContentWidth="320px" />}
        <div style={{ float: 'left', clear: 'both', height: 20 }} ref={messagesEndRef}></div>
      </>
    )}
  </MessagesContainer>
);

export default MessagesArea;

const MessagesContainer = styled.div`
  position: relative;
  flex: 1;
  overflow-y: auto;
  padding: 24px 12px 0px;
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  line-height: 1.55;

  & * {
    font-family: 'Poppins', sans-serif;
  }
`;

const WelcomeMessage = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  flex: 1;
  min-height: 0;
  text-align: center;
  padding: 0px 18px 60px;
  background: #ffffff;
  border-radius: 16px;
`;

const WelcomeTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 6px;
`;

const WelcomeText = styled.div`
  font-size: 13px;
  color: #777777;
  line-height: 1.55;
  max-width: 560px;
  margin-bottom: 14px;
`;

const WelcomeFooter = styled.div`
  position: absolute;
  bottom: 32px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const ErrorMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px;
`;

const ErrorText = styled.div`
  color: #ff4444;
  margin-bottom: 16px;
  text-align: center;
`;

const ConnectingMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666d79;
`;

const Disclaimer = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: #777777;
  line-height: 1.4;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 320px;
  white-space: nowrap;
`;

const PromptSection = styled.div`
  width: 100%;
  margin-top: 20px;
  text-align: left;
`;

const PromptSectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333333;
`;

const PromptList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PromptCard = styled.button`
  width: 100%;
  text-align: left;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 14px;
  cursor: pointer;
  color: #111827;
  transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);

  &:hover {
    background-color: #f8fafc;
    border-color: #cbd5e1;
    transform: translateY(-1px);
  }
`;

const PromptCardTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 6px;
  color: #333333;
`;

const PromptCardText = styled.div`
  font-size: 12px;
  line-height: 1.65;
  color: #777777;
  white-space: pre-wrap;
`;
